import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { handleApiError } from "../../utils/apiErrorHandler";
import { buildCsv, downloadCsv } from "../../utils/csvExport";
import { EXPORT_PAGE_SIZE } from "../../utils/paginatedExport";

/**
 * Generic "export a list to CSV" modal, shared by the Companies and Users pages.
 *
 * @param {Object} props
 * @param {boolean}  props.activeModal
 * @param {Function} props.setActiveModal   called with no args to close
 * @param {string}   props.title            e.g. "Export Companies"
 * @param {Array}    props.tabs             [{ key, label, icon }] — rendered as radios
 * @param {string}   props.defaultTab       preselected tab key
 * @param {Function} props.filterSummary    (tab) => ({ items: [{label, value}], notes: [string] })
 * @param {Function} props.fetchRows        ({ tab, fromPage, toPage, pageSize, onProgress, isCancelled }) => Promise<rows>
 * @param {Array}    props.columns          [{ header, value }] for buildCsv
 * @param {Function} props.buildFilename    (tab, scopeParts) => string
 * @param {Object}   props.entityLabel      { one, many } used in the success toast
 * @param {Object}   props.pageScope        { tab, currentPage, pageSize, totalPages, totalCount }
 *   of the tab currently displayed on the page — drives the page-range options
 */
const ExportModal = ({
  activeModal,
  setActiveModal,
  title = "Export",
  icon = "fa-download",
  tabs = [],
  defaultTab,
  filterSummary,
  fetchRows,
  columns = [],
  buildFilename,
  entityLabel = { one: "record", many: "records" },
  pageScope = null,
}) => {
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [scope, setScope] = useState("all");
  const [rangeFrom, setRangeFrom] = useState("1");
  const [rangeTo, setRangeTo] = useState("1");
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const cancelledRef = useRef(false);

  const totalPages = pageScope?.totalPages || 0;
  const viewedPage = pageScope?.currentPage || 1;

  // Page numbers only mean something for the tab that is actually on screen.
  const pageOptionsAvailable = Boolean(pageScope) && selectedTab === pageScope.tab && totalPages > 0;

  // Preselect whichever tab the user is currently looking at.
  useEffect(() => {
    if (activeModal) {
      setSelectedTab(defaultTab);
      setScope("all");
      setRangeFrom(String(viewedPage));
      setRangeTo(String(Math.min(viewedPage + 1, totalPages || viewedPage)));
      setProgress(null);
      cancelledRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, defaultTab]);

  // Switching to a tab we are not viewing invalidates the page options.
  useEffect(() => {
    if (!pageOptionsAvailable && scope !== "all") setScope("all");
  }, [pageOptionsAvailable, scope]);

  const handleClose = () => {
    if (isExporting) return;
    cancelledRef.current = true;
    setActiveModal();
  };

  const summary = (filterSummary && filterSummary(selectedTab)) || {};
  const items = summary.items || [];
  const notes = summary.notes || [];

  const parsedFrom = parseInt(rangeFrom, 10);
  const parsedTo = parseInt(rangeTo, 10);

  const rangeError =
    scope !== "range"
      ? null
      : !Number.isInteger(parsedFrom) || !Number.isInteger(parsedTo)
        ? "Enter both page numbers."
        : parsedFrom < 1
          ? "The first page must be 1 or higher."
          : parsedTo < parsedFrom
            ? "The last page must not be before the first."
            : parsedTo > totalPages
              ? `There ${totalPages === 1 ? "is" : "are"} only ${totalPages} page${totalPages === 1 ? "" : "s"}.`
              : null;

  // Resolve the chosen scope into loop bounds. "all" leaves pageSize unset so
  // fetchAllPaginated uses its own larger page size (fewer round-trips).
  const resolveScope = () => {
    if (scope === "current") {
      return { fromPage: viewedPage, toPage: viewedPage, pageSize: pageScope.pageSize };
    }
    if (scope === "range") {
      return { fromPage: parsedFrom, toPage: parsedTo, pageSize: pageScope.pageSize };
    }
    return { fromPage: 1, toPage: null, pageSize: undefined };
  };

  const scopeFilenameParts = () => {
    if (scope === "current") return ["page", String(viewedPage)];
    if (scope === "range")
      return parsedFrom === parsedTo
        ? ["page", String(parsedFrom)]
        : ["pages", String(parsedFrom), String(parsedTo)];
    return [];
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setProgress(null);
      cancelledRef.current = false;

      const { fromPage, toPage, pageSize } = resolveScope();

      const rows = await fetchRows({
        tab: selectedTab,
        fromPage,
        toPage,
        pageSize,
        onProgress: setProgress,
        isCancelled: () => cancelledRef.current,
      });

      if (cancelledRef.current) return;

      if (rows.length === 0) {
        toast.error(`No ${entityLabel.many} found to export`);
        return;
      }

      // Keep the table's own row numbering for a partial export.
      const startIndex = (fromPage - 1) * (pageSize || EXPORT_PAGE_SIZE);

      downloadCsv(
        buildFilename(selectedTab, scopeFilenameParts()),
        buildCsv(rows, columns, startIndex)
      );

      toast.success(
        `Exported ${rows.length} ${rows.length === 1 ? entityLabel.one : entityLabel.many}`
      );
      setActiveModal();
    } catch (error) {
      if (error?.response || error?.request) {
        handleApiError(error);
      } else {
        toast.error(error?.message || `Failed to export ${entityLabel.many}`);
        console.error("Export error:", error);
      }
    } finally {
      setIsExporting(false);
      setProgress(null);
    }
  };

  const progressPercent =
    progress && progress.totalCount
      ? Math.min(100, Math.round((progress.fetched / progress.totalCount) * 100))
      : 0;

  return (
    <Modal show={activeModal} onHide={handleClose} backdrop="static" centered>
      <Modal.Header closeButton={!isExporting}>
        <Modal.Title>
          <i className={`fa ${icon} me-2 mr-2`}></i>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-2">
          <strong>Which records do you want to export?</strong>
        </p>

        {tabs.map((tab) => (
          <div className="form-check mb-2" key={tab.key}>
            <input
              className="form-check-input"
              type="radio"
              name="exportRecordType"
              id={`export-option-${tab.key}`}
              value={tab.key}
              checked={selectedTab === tab.key}
              onChange={() => setSelectedTab(tab.key)}
              disabled={isExporting}
            />
            <label className="form-check-label" htmlFor={`export-option-${tab.key}`}>
              <i className={`fa ${tab.icon} me-1 mr-2`}></i>
              {tab.label}
            </label>
          </div>
        ))}

        {pageScope && (
          <>
            <hr className="my-3" />
            <p className="mb-2">
              <strong>Rows to export</strong>
            </p>

            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="radio"
                name="exportScope"
                id="export-scope-all"
                checked={scope === "all"}
                onChange={() => setScope("all")}
                disabled={isExporting}
              />
              <label className="form-check-label" htmlFor="export-scope-all">
                All pages
                {pageScope.totalCount > 0 && selectedTab === pageScope.tab && (
                  <small className="text-muted"> ({pageScope.totalCount} records)</small>
                )}
              </label>
            </div>

            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="radio"
                name="exportScope"
                id="export-scope-current"
                checked={scope === "current"}
                onChange={() => setScope("current")}
                disabled={isExporting || !pageOptionsAvailable}
              />
              <label className="form-check-label" htmlFor="export-scope-current">
                Current page{pageOptionsAvailable ? ` (${viewedPage} of ${totalPages})` : ""}
              </label>
            </div>

            <div className="form-check mb-1">
              <input
                className="form-check-input"
                type="radio"
                name="exportScope"
                id="export-scope-range"
                checked={scope === "range"}
                onChange={() => setScope("range")}
                disabled={isExporting || !pageOptionsAvailable}
              />
              <label className="form-check-label" htmlFor="export-scope-range">
                Page range
              </label>
            </div>

            {scope === "range" && (
              <div className="d-flex align-items-center gap-2 ms-4 mt-2" style={{ gap: "8px" }}>
                <span className="text-muted">Pages</span>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: "80px" }}
                  min="1"
                  max={totalPages}
                  value={rangeFrom}
                  onChange={(e) => setRangeFrom(e.target.value)}
                  disabled={isExporting}
                  aria-label="First page to export"
                />
                <span className="text-muted">to</span>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: "80px" }}
                  min="1"
                  max={totalPages}
                  value={rangeTo}
                  onChange={(e) => setRangeTo(e.target.value)}
                  disabled={isExporting}
                  aria-label="Last page to export"
                />
                <span className="text-muted">of {totalPages}</span>
              </div>
            )}

            {rangeError && (
              <div className="mt-2 ms-4">
                <small className="text-danger">{rangeError}</small>
              </div>
            )}

            {!pageOptionsAvailable && pageScope.tab && selectedTab !== pageScope.tab && (
              <div className="mt-2">
                <small className="text-muted">
                  <i className="fa fa-info-circle me-1 mr-2"></i>
                  Page options apply to the tab you are viewing (
                  {tabs.find((t) => t.key === pageScope.tab)?.label || pageScope.tab}).
                </small>
              </div>
            )}
          </>
        )}

        <div className="alert alert-info mt-3 mb-0">
          {items.length > 0 ? (
            <>
              <strong>Filters applied:</strong>
              <ul className="mb-0 mt-1">
                {items.map((item) => (
                  <li key={item.label}>
                    {item.label}: <strong>{item.value}</strong>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <span>No filters applied — exporting all records.</span>
          )}
        </div>

        {notes.map((note) => (
          <div className="mt-2" key={note}>
            <small className="text-muted">
              <i className="fa fa-info-circle me-1 mr-2"></i>
              {note}
            </small>
          </div>
        ))}

        {isExporting && (
          <div className="mt-3">
            <small className="text-muted">
              {progress
                ? `Fetched ${progress.fetched} of ${progress.totalCount} ${entityLabel.many}...`
                : `Fetching ${entityLabel.many}...`}
            </small>
            <div className="progress mt-1" style={{ height: "6px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${progressPercent || 10}%` }}
              ></div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleClose}
          disabled={isExporting}
        >
          <i className="fa fa-times me-2 mr-2"></i>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleExport}
          disabled={isExporting || Boolean(rangeError)}
        >
          {isExporting ? (
            <>
              <i className="fa fa-spinner fa-spin me-2 mr-2"></i>
              Exporting...
            </>
          ) : (
            <>
              <i className="fa fa-download me-2 mr-2"></i>
              Export CSV
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportModal;
